/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from 'react';
import { Box, Button } from '@chakra-ui/react';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { fileName, excelSale } from '../../Miscellaneous/export';
import ReactExport from 'react-export-excel';
import c from '../../../../constant';
import useStore from '../../../../store';
import { toFormatCurrency } from '../../../../utils/format';

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

export default function ExportExcel({ month = null, year = null, numColumns = 0, saleItems = [], mtplItems = null, natCatItems = null, ...props }) {
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);

	const data = useMemo(() => {
		const sales = saleItems?.map((cur) => excelData(cur?.saleReportType, cur?.saleProducts));
		const mtpls = excelDataMtpl(mtplItems);
		const natcat = excelDataNatCat(natCatItems);

		return [...sales, mtpls, natcat].filter(Boolean);
	}, [saleItems, mtplItems, natCatItems, lang, t, numColumns]);

	function excelData(saleReportType = null, saleProducts = []) {
		const news = excelSale({
			title: t('New Policy'),
			saleProducts: (saleProducts || [])
				?.filter((el) => el?.policyType === 'New')
				?.sort((a, b) => (parseInt(a?.productLine?.order) > parseInt(b?.productLine?.order) ? 1 : -1)),
			numColumns,
			total: t('Total'),
			lang,
		});
		const renewals = excelSale({
			title: t('Renewal Policy'),
			saleProducts: (saleProducts || [])
				?.filter((el) => el?.policyType === 'Renewal')
				?.sort((a, b) => (parseInt(a?.productLine?.order) > parseInt(b?.productLine?.order) ? 1 : -1)),
			numColumns,
			total: t('Total'),
			lang,
		});
		return { name: saleReportType, data: [...news, ...renewals] };
	}

	function excelDataMtpl(items = null) {
		if (!items) return null;

		let provinces = items?.provinces?.map((province) => ({
			name: province?.name,
			latinName: province?.latinName,
		}));

		provinces.unshift({ name: t('Total'), latinName: t('Total') });
		provinces.unshift({ name: 'ប្រភេទការធានារ៉ាប់រងកាតព្វកិច្ច', latinName: 'Type of MTPL Insurance' });

		let data = items?.cells?.map((row, i) => {
			const isReadOnly = items?.labels?.[i]?.readOnly;

			const total = (row || []).reduce((sum, cell) => {
				const data = String(cell?.value ?? '')
					.replace(/,/g, '')
					.trim();

				if (data === '') return sum;

				const value = Number(data);
				return Number.isFinite(value) ? sum + value : sum;
			}, 0);

			return [
				lang === 'kh' ? items?.labels?.[i]?.titleKhmer : items?.labels?.[i]?.titleEnglish,
				isReadOnly ? '' : toFormatCurrency(total, null),
				...(row?.map((cell) => cell?.value) || []),
			];
		});

		return { name: 'MTPL', provinces, data };
	}

	function excelDataNatCat(items = null) {
		if (items) {
			let values = items?.cells?.map((values) => values?.map((valueType) => valueType?.value));
			let data = values?.map((value, i) => {
				let localValue = value;
				localValue.unshift(lang === 'kh' ? items?.provinces[i]?.name : items?.provinces[i]?.latinName);
				localValue.unshift(lang === 'kh' ? '' : '');
				return localValue;
			});

			let totalNatCat = items?.cells?.map((values) => values?.map((valueType) => valueType?.value));
			const totals = totalNatCat.reduce((acc, row) => {
				row.forEach((val, colIndex) => {
					acc[colIndex] = (acc[colIndex] || 0) + val;
				});
				return acc;
			}, []);

			data = [...data, ['', 'Total', ...totals]];

			return { name: 'NATCAT', data };
		}
		return null;
	}

	return (
		<Box {...props}>
			<ExcelFile
				filename={fileName('Sale', 'General', month, null, year)}
				element={
					<Button
						leftIcon={<FontAwesomeIcon icon={faFileExcel} style={{ fontSize: 16 }} />}
						colorScheme="gray"
						variant="solid"
						size="sm"
						borderRadius={c.borderRadius}
						border="1px solid #bdc3c7"
					>
						{t('Export')}
					</Button>
				}
			>
				{data?.map((el, i) => {
					if (el?.name === 'MTPL') {
						return (
							<ExcelSheet key={`mtpl-${i}`} data={el?.data} name={t('MTPL')}>
								{el?.provinces?.map((e, i) => {
									return <ExcelColumn key={i} label={lang === 'kh' ? e?.name : e?.latinName} value={`${i}`} />;
								})}
							</ExcelSheet>
						);
					} else if (el?.name === 'NATCAT') {
						return (
							<ExcelSheet key={`natcat-${i}`} data={el?.data} name={t('NATCAT')}>
								<ExcelColumn label={t('Province/City')} value="1" />
								<ExcelColumn
									label={lang === 'kh' ? 'បុព្វលាភធានារ៉ាប់រងដុល (គ្រោះមហន្តរាយសម្រាប់ធានារ៉ាប់រងទូទៅ)' : 'Gross Premium (Nat Cat)'}
									value="2"
								/>
								<ExcelColumn
									label={lang === 'kh' ? 'ចំនួនបណ្ណសន្យារ៉ាប់រង (គ្រោះមហន្តរាយសម្រាប់ធានារ៉ាប់រងទូទៅ)' : 'Number of Policies (Nat Cat)'}
									value="3"
								/>
								<ExcelColumn
									label={lang === 'kh' ? 'ទឹកប្រាក់ដែលត្រូវបានធានារ៉ាប់រង (គ្រោះមហន្តរាយសម្រាប់ធានារ៉ាប់រងទូទៅ)' : 'Sum Insured (Nat Cat)'}
									value="4"
								/>
								<ExcelColumn
									label={
										lang === 'kh'
											? 'បុព្វលាភធានារ៉ាប់រងដុល (គ្រោះមហន្តរាយសម្រាប់ទ្រព្យសម្បត្តិ)'
											: 'Gross Premium (Nat Cat for Property and Casualty)'
									}
									value="5"
								/>
								<ExcelColumn
									label={
										lang === 'kh'
											? 'ចំនួនបណ្ណសន្យារ៉ាប់រង (គ្រោះមហន្តរាយសម្រាប់ទ្រព្យសម្បត្តិ)'
											: 'Number of Policies (Nat Cat for Property and Casualty)'
									}
									value="6"
								/>
								<ExcelColumn
									label={
										lang === 'kh'
											? 'ទឹកប្រាក់ដែលត្រូវបានធានារ៉ាប់រង (គ្រោះមហន្តរាយសម្រាប់កសិកម្ម)'
											: 'Sum Insured (Nat Cat for Property and Casualty)'
									}
									value="7"
								/>
								<ExcelColumn
									label={lang === 'kh' ? 'បុព្វលាភធានារ៉ាប់រងដុល (គ្រោះមហន្តរាយសម្រាប់កសិកម្ម)' : 'Gross Premium (Nat Cat for Agriculture)'}
									value="8"
								/>
								<ExcelColumn
									label={
										lang === 'kh' ? 'ចំនួនបណ្ណសន្យារ៉ាប់រង (គ្រោះមហន្តរាយសម្រាប់កសិកម្ម)' : 'Number of Policies (Nat Cat for Agriculture)'
									}
									value="9"
								/>
								<ExcelColumn
									label={
										lang === 'kh'
											? 'ទឹកប្រាក់ដែលត្រូវបានធានារ៉ាប់រង (គ្រោះមហន្តរាយសម្រាប់កសិកម្ម)'
											: 'Sum Insured (Nat Cat for Agriculture)'
									}
									value="10"
								/>
							</ExcelSheet>
						);
					}

					return (
						<ExcelSheet key={`gen-${i}`} data={el?.data} name={t(el?.name)}>
							<ExcelColumn label={t('Ordinal')} value="0" />
							<ExcelColumn label={t('Product Line')} value="1" />
							<ExcelColumn
								label={lang === 'kh' ? '> $5,000 (បណ្ណសន្យារ៉ាប់រង_បុព្វលាភធានារ៉ាប់រងដុល)' : '> $5000 (Policy_Gross Premium)'}
								value="2"
							/>
							<ExcelColumn
								label={lang === 'kh' ? '≤ $5,000 (បណ្ណសន្យារ៉ាប់រង_បុព្វលាភធានារ៉ាប់រងដុល)' : '≤ $5000 (Policy_Gross Premium)'}
								value="3"
							/>
							<ExcelColumn
								label={lang === 'kh' ? '> $5,000 (បណ្ណសន្យារ៉ាប់រង_ទឹកប្រាក់ធានារ៉ាប់រង)' : '> $5000 (Policy_Sum insured)'}
								value="4"
							/>
							<ExcelColumn
								label={lang === 'kh' ? '≤ $5,000 (បណ្ណសន្យារ៉ាប់រង_ទឹកប្រាក់ធានារ៉ាប់រង)' : '≤ $5000 (Policy_Sum insured)'}
								value="5"
							/>
							<ExcelColumn
								label={lang === 'kh' ? '> $5,000 (បណ្ណសន្យារ៉ាប់រង_ចំនួនបណ្ណសន្យារ៉ាប់រង)' : '> $5000 (Policy_Number of Policies)'}
								value="6"
							/>
							<ExcelColumn
								label={lang === 'kh' ? '≤ $5,000 (បណ្ណសន្យារ៉ាប់រង_ចំនួនបណ្ណសន្យារ៉ាប់រង)' : '≤ $5000 (Policy_Number of Policies)'}
								value="7"
							/>
							<ExcelColumn
								label={lang === 'kh' ? '> $5,000 ប្រុស (បណ្ណសន្យារ៉ាប់រង_ចំនួនដែលត្រូវបានធានា)' : '> $5000 Male (Policy_Number of Insured)'}
								value="8"
							/>
							<ExcelColumn
								label={lang === 'kh' ? '> $5,000 ស្រី (បណ្ណសន្យារ៉ាប់រង_ចំនួនដែលត្រូវបានធានា)' : '> $5000 Female (Policy_Number of Insured)'}
								value="9"
							/>
							<ExcelColumn
								label={
									lang === 'kh'
										? '> $5,000 ឯកតាផ្សេង (បណ្ណសន្យារ៉ាប់រង_ចំនួនដែលត្រូវបានធានា)'
										: '> $5000 Other Unit (Policy_Number of Insured)'
								}
								value="10"
							/>
							<ExcelColumn
								label={lang === 'kh' ? '≤ $5,000 ប្រុស (បណ្ណសន្យារ៉ាប់រង_ចំនួនដែលត្រូវបានធានា)' : '≤ $5000 Male (Policy_Number of Insured)'}
								value="11"
							/>
							<ExcelColumn
								label={lang === 'kh' ? '≤ $5,000 ស្រី (បណ្ណសន្យារ៉ាប់រង_ចំនួនដែលត្រូវបានធានា)' : '≤ $5000 Female (Policy_Number of Insured)'}
								value="12"
							/>
							<ExcelColumn
								label={
									lang === 'kh'
										? '≤ $5,000 ឯកតាផ្សេង (បណ្ណសន្យារ៉ាប់រង_ចំនួនដែលត្រូវបានធានា)'
										: '≤ $5000 Other Unit (Policy_Number of Insured)'
								}
								value="13"
							/>
							<ExcelColumn
								label={
									lang === 'kh'
										? '> $5,000 (បណ្ណសន្យារ៉ាប់រងលុបចោល/បញ្ចប់_ចំនួនបណ្ណសន្យារ៉ាប់រងបញ្ចប់ក្នុងរយៈពេលសាកល្បង)'
										: '> $5000 (Cancelled Policies_Number of Cancellation)'
								}
								value="14"
							/>
							<ExcelColumn
								label={
									lang === 'kh'
										? '≤ $5,000 (បណ្ណសន្យារ៉ាប់រងលុបចោល/បញ្ចប់_ចំនួនបណ្ណសន្យារ៉ាប់រងបញ្ចប់ក្នុងរយៈពេលសាកល្បង)'
										: '≤ $5000 (Cancelled Policies_Number of Cancellation)'
								}
								value="15"
							/>
							<ExcelColumn
								label={
									lang === 'kh'
										? '> $5,000 (បណ្ណសន្យារ៉ាប់រងលុបចោល/បញ្ចប់_បុព្វលាភធានារ៉ាប់រងដុល)'
										: '> $5000 (Cancelled Policies_Gross Premium)'
								}
								value="16"
							/>
							<ExcelColumn
								label={
									lang === 'kh'
										? '≤ $5,000 (បណ្ណសន្យារ៉ាប់រងលុបចោល/បញ្ចប់_បុព្វលាភធានារ៉ាប់រងដុល)'
										: '≤ $5000 (Cancelled Policies_Gross Premium)'
								}
								value="17"
							/>
						</ExcelSheet>
					);
				})}
			</ExcelFile>
		</Box>
	);
}
